import { useState, useMemo, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import {
  Close,
  Add,
  Send,
  ChevronLeft,
  ChevronRight,
  PlayArrow,
  Pause,
  PlayCircleOutline,
} from '@mui/icons-material'
import { useSessionJournal } from '../../../hooks/useSessionJournal'
import { useMarkJournalRead } from '../../../hooks/useSession'
import { useSessionStore } from '../../../stores/sessionStore'
import { JournalEntryDto, JournalEntryType } from '@api/types/session'

interface JournalPanelProps {
  sessionId: string | null
  onClose: () => void
}

// Entry types that should be visible to users
export const VISIBLE_ENTRY_TYPES = [
  JournalEntryType.FormattedMessage,
  JournalEntryType.TextClue,
  JournalEntryType.ClueRequested,
  JournalEntryType.UserNote,
  JournalEntryType.ChoiceMade,
  JournalEntryType.PoiLocation,
  JournalEntryType.ImageDisplayed,
  JournalEntryType.AudioPlayed,
  JournalEntryType.VideoPlayed,
  JournalEntryType.SessionCompleted,
  JournalEntryType.SessionFailed,
  JournalEntryType.ModeratorApprovalReceived,
]

// Estimate how many entries fit on a page (rough approximation)
const ENTRIES_PER_PAGE = 3

export default function JournalPanel({ sessionId, onClose }: JournalPanelProps) {
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [currentPage, setCurrentPage] = useState(0)
  const [playingAudio, setPlayingAudio] = useState<string | null>(null)
  const [videoDialog, setVideoDialog] = useState<{ open: boolean; url: string; title: string }>({
    open: false,
    url: '',
    title: '',
  })
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const {
    journal,
    isLoading,
    addNote,
    isAddingNote,
  } = useSessionJournal(sessionId)

  const markJournalRead = useMarkJournalRead()
  const latestJournalSequenceNumber = useSessionStore((s) => s.latestJournalSequenceNumber)

  // Mark journal as read when panel opens
  useEffect(() => {
    if (sessionId && latestJournalSequenceNumber > 0) {
      markJournalRead.mutate({
        sessionId,
        data: { sequenceNumber: latestJournalSequenceNumber },
      })
    }
  }, [sessionId, latestJournalSequenceNumber])

  // Filter to only show user-relevant entries
  const visibleEntries = useMemo(() => {
    return journal.filter(entry => VISIBLE_ENTRY_TYPES.includes(entry.entryType))
  }, [journal])

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(visibleEntries.length / ENTRIES_PER_PAGE))

  // Get entries for current page
  const currentPageEntries = useMemo(() => {
    const start = currentPage * ENTRIES_PER_PAGE
    return visibleEntries.slice(start, start + ENTRIES_PER_PAGE)
  }, [visibleEntries, currentPage])

  // Auto-navigate to last page when new entries are added
  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(visibleEntries.length / ENTRIES_PER_PAGE))
    if (currentPage >= newTotalPages) {
      setCurrentPage(Math.max(0, newTotalPages - 1))
    }
  }, [visibleEntries.length, currentPage])

  const handleAddNote = () => {
    if (noteText.trim()) {
      addNote(noteText.trim())
      setNoteText('')
      setShowNoteForm(false)
    }
  }

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))
  }

  const handlePlayAudio = (url: string, entryId: string) => {
    if (playingAudio === entryId) {
      // Pause current audio
      audioRef.current?.pause()
      setPlayingAudio(null)
    } else {
      // Play new audio
      if (audioRef.current) {
        audioRef.current.src = url
        audioRef.current.play()
        setPlayingAudio(entryId)
      }
    }
  }

  const handleAudioEnded = () => {
    setPlayingAudio(null)
  }

  const handleOpenVideo = (url: string, title: string) => {
    setVideoDialog({ open: true, url, title })
  }

  return (
    <Paper
      elevation={8}
      sx={{
        width: 380,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      {/* Hidden audio element */}
      <audio ref={audioRef} onEnded={handleAudioEnded} />

      {/* Header - styled like notebook top */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          Journal
        </Typography>
        <Box>
          <IconButton
            size="small"
            onClick={() => setShowNoteForm(!showNoteForm)}
            color={showNoteForm ? 'primary' : 'default'}
          >
            <Add />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Add note form */}
      {showNoteForm && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={isAddingNote}
            size="small"
            sx={{
              mb: 1,
              '& .MuiInputBase-root': {
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.875rem',
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => {
                setShowNoteForm(false)
                setNoteText('')
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={isAddingNote ? <CircularProgress size={16} /> : <Send />}
              onClick={handleAddNote}
              disabled={!noteText.trim() || isAddingNote}
            >
              Add
            </Button>
          </Box>
        </Box>
      )}

      {/* Journal page content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          py: 2,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : visibleEntries.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              color: 'text.secondary',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              Your journal is empty...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                mt: 1,
                color: 'text.disabled',
              }}
            >
              Notes and discoveries will appear here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {currentPageEntries.map((entry) => (
              <JournalEntryContent
                key={entry.id}
                entry={entry}
                playingAudio={playingAudio}
                onPlayAudio={handlePlayAudio}
                onOpenVideo={handleOpenVideo}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Page navigation */}
      {visibleEntries.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            gap: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={handlePrevPage}
            disabled={currentPage === 0}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Page {currentPage + 1} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages - 1}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      {/* Video dialog */}
      <Dialog
        open={videoDialog.open}
        onClose={() => setVideoDialog({ open: false, url: '', title: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{videoDialog.title || 'Video'}</DialogTitle>
        <DialogContent>
          {videoDialog.url && (
            <video
              src={videoDialog.url}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  )
}

// Individual journal entry content renderer
export interface JournalEntryContentProps {
  entry: JournalEntryDto
  playingAudio: string | null
  onPlayAudio: (url: string, entryId: string) => void
  onOpenVideo: (url: string, title: string) => void
}

export function JournalEntryContent({ entry, playingAudio, onPlayAudio, onOpenVideo }: JournalEntryContentProps) {
  const isPlaying = playingAudio === entry.id

  // Resolve content fields — top-level parsed fields may be empty when loading
  // from the journal API (GET /sessions/{id}/journal), so fall back to content object
  const c = entry.content as Record<string, unknown> | null
  const resolvedHtmlContent = entry.htmlContent || (c?.htmlContent as string) || (c?.HtmlContent as string) || null
  const resolvedTextContent = entry.textContent || (c?.textContent as string) || (c?.TextContent as string) || null

  // Common text styling for journal feed
  const textStyle = {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: 'text.primary',
  }

  switch (entry.entryType) {
    case JournalEntryType.FormattedMessage:
      return (
        <Box
          sx={{
            p: 2,
            background: (theme) => `linear-gradient(135deg, ${theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.08)' : 'rgba(212,168,83,0.06)'}, ${theme.palette.mode === 'dark' ? 'rgba(212,168,83,0.03)' : 'rgba(212,168,83,0.02)'})`,
            borderLeft: 3,
            borderColor: 'warning.main',
            borderRadius: '0 10px 10px 0',
          }}
        >
          <Typography sx={{
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: 'warning.main',
            mb: 1,
          }}>
            ◆ Narrator
          </Typography>
          {resolvedHtmlContent ? (
            <Box
              sx={{
                fontFamily: "'Lora', serif",
                fontSize: '0.875rem',
                lineHeight: 1.7,
                color: 'text.primary',
                '& p': { margin: '0.5em 0' },
                '& strong, & b': { fontWeight: 700 },
                '& em, & i': { fontStyle: 'italic' },
                '& pre': {
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '8px',
                  borderRadius: '4px',
                  margin: '0.5em 0',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& code': {
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                  borderRadius: 0,
                },
                '& blockquote': {
                  borderLeft: 4,
                  borderColor: 'primary.main',
                  margin: '0.5em 0',
                  paddingLeft: '12px',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                },
                '& blockquote p': {
                  margin: '0.25em 0',
                },
                '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
                '& td, & th': { p: 1, verticalAlign: 'top' },
                '& table:not([data-borderless="true"]) td, & table:not([data-borderless="true"]) th': { border: '1px solid', borderColor: 'divider' },
                '& table:not([data-borderless="true"]) th': { bgcolor: 'action.hover', fontWeight: 600 },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  mt: 1.5,
                },
              }}
              dangerouslySetInnerHTML={{ __html: resolvedHtmlContent }}
            />
          ) : (
            <Typography sx={{ fontFamily: "'Lora', serif", fontSize: '0.875rem', lineHeight: 1.7, color: 'text.primary' }}>
              {resolvedTextContent || 'Message received'}
            </Typography>
          )}
        </Box>
      )

    case JournalEntryType.TextClue:
      return (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 1, py: 0.75, fontSize: '0.6875rem', color: 'text.disabled',
          textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500,
        }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          <span>🔓</span>
          <span>New clues unlocked</span>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        </Box>
      )

    case JournalEntryType.ClueRequested: {
      const crHtml = entry.clueHtmlContent || (entry.content as any)?.content || (entry.content as any)?.Content || (entry.content as any)?.htmlContent || (entry.content as any)?.HtmlContent
      return (
        <Box
          sx={{
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(66, 153, 225, 0.06)' : 'rgba(66, 153, 225, 0.08)',
            border: 1,
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(66, 153, 225, 0.15)' : 'rgba(66, 153, 225, 0.2)',
            borderRadius: 2.5,
            p: 1.5,
            display: 'flex',
            gap: 1.25,
          }}
        >
          <Box sx={{
            width: 32, height: 32,
            background: 'rgba(66, 153, 225, 0.15)',
            borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: '14px',
          }}>💡</Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'info.main', mb: 0.5 }}>
              Hint Used
            </Typography>
            {crHtml ? (
              <Box
                dangerouslySetInnerHTML={{ __html: crHtml }}
                sx={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'text.secondary', fontStyle: 'italic', '& p': { mt: 0, mb: 0.5 } }}
              />
            ) : (
              <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'text.secondary', fontStyle: 'italic' }}>
                {entry.textContent || 'A clue was requested'}
              </Typography>
            )}
          </Box>
        </Box>
      )
    }

    case JournalEntryType.UserNote:
      return (
        <Box sx={{
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2.5,
          p: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}>
          <Box sx={{ flexShrink: 0, fontSize: '14px' }}>📝</Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                ...textStyle,
                fontStyle: 'italic',
                color: 'text.secondary',
                fontSize: '0.8rem',
              }}
            >
              {entry.note || entry.textContent || 'Personal note'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                display: 'block',
                mt: 0.5,
              }}
            >
              — {new Date(entry.createdAt).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )

    case JournalEntryType.ChoiceMade:
      // Parse content - can be object or string
      const choiceContent = typeof entry.content === 'string'
        ? JSON.parse(entry.content)
        : entry.content || {}
      // Result contains the text, Choice contains the GUID
      const userChoice = choiceContent.Result || choiceContent.result || choiceContent.Choice || choiceContent.choice || 'Unknown'
      const branchKey = choiceContent.BranchKey || choiceContent.branchKey || ''
      const isCorrect = choiceContent.IsCorrect ?? choiceContent.isCorrect ?? (branchKey === 'correct')
      const wasSkipped = branchKey === 'skip'
      const wasCancelled = branchKey === 'cancel'
      const wasTimeout = branchKey === 'timeout'

      // Determine display style based on result
      const isNeutral = wasSkipped || wasCancelled || wasTimeout
      const bgColor = isNeutral
        ? 'rgba(158, 158, 158, 0.1)'
        : isCorrect
          ? 'rgba(76, 175, 80, 0.1)'
          : 'rgba(244, 67, 54, 0.1)'
      const borderColor = isNeutral
        ? 'grey.400'
        : isCorrect
          ? 'success.light'
          : 'error.light'
      const resultColor = isNeutral
        ? 'text.secondary'
        : isCorrect
          ? 'success.main'
          : 'error.main'

      // Determine result text - combined with answer on one line
      let resultText = ''
      if (wasSkipped) resultText = '⏭ Skipped'
      else if (wasCancelled) resultText = '✗ Cancelled'
      else if (wasTimeout) resultText = '⏱ Timed out'
      else if (isCorrect) resultText = `✓ Correct Answer: ${userChoice}`
      else resultText = `✗ Incorrect Answer: ${userChoice}`

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.25,
            borderRadius: 2.5,
            bgcolor: bgColor,
            border: 1,
            borderColor: borderColor,
          }}
        >
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', flexShrink: 0,
            bgcolor: isNeutral ? 'rgba(158,158,158,0.2)' : isCorrect ? 'rgba(72,187,120,0.2)' : 'rgba(252,129,129,0.2)',
          }}>
            {isNeutral ? (wasSkipped ? '⏭' : wasCancelled ? '✗' : '⏱') : isCorrect ? '✓' : '✗'}
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: '0.8rem',
              fontWeight: 600,
              color: resultColor,
            }}
          >
            {resultText}
          </Typography>
        </Box>
      )

    case JournalEntryType.PoiLocation:
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            borderRadius: 1,
          }}
        >
          <Typography sx={textStyle}>
            📍 A new location has been added to your map
          </Typography>
        </Box>
      )

    case JournalEntryType.ImageDisplayed:
      const imageUrl = entry.content?.url || (entry.content as any)?.imageUrl
      return (
        <Box sx={{ textAlign: 'center' }}>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt="Journal image"
              sx={{
                maxWidth: '66%',
                height: 'auto',
                borderRadius: 1,
                boxShadow: 2,
              }}
            />
          ) : (
            <Typography sx={textStyle}>An image was displayed</Typography>
          )}
          {entry.content?.caption && (
            <Typography
              sx={{
                ...textStyle,
                fontSize: '0.95rem',
                fontStyle: 'italic',
                mt: 1,
              }}
            >
              {(entry.content as any).caption}
            </Typography>
          )}
        </Box>
      )

    case JournalEntryType.AudioPlayed:
      const audioUrl = entry.content?.url || (entry.content as any)?.audioUrl
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(156, 39, 176, 0.1)',
            borderRadius: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => audioUrl && onPlayAudio(audioUrl, entry.id)}
            disabled={!audioUrl}
            color="primary"
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <Typography sx={textStyle}>
            {(entry.content as any)?.title || 'Audio recording'}
          </Typography>
        </Box>
      )

    case JournalEntryType.VideoPlayed:
      const videoUrl = entry.content?.url || (entry.content as any)?.videoUrl
      const videoTitle = (entry.content as any)?.title || 'Video'
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(233, 30, 99, 0.1)',
            borderRadius: 1,
            cursor: videoUrl ? 'pointer' : 'default',
          }}
          onClick={() => videoUrl && onOpenVideo(videoUrl, videoTitle)}
        >
          <PlayCircleOutline color="primary" />
          <Typography sx={textStyle}>
            {videoTitle} — tap to play
          </Typography>
        </Box>
      )

    case JournalEntryType.SessionCompleted:
      return (
        <Box
          sx={{
            textAlign: 'center',
            p: 2,
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ ...textStyle, fontSize: '1.3rem', color: 'success.main' }}>
            🎉 Adventure Completed!
          </Typography>
        </Box>
      )

    case JournalEntryType.SessionFailed:
      return (
        <Box
          sx={{
            textAlign: 'center',
            p: 2,
            bgcolor: 'rgba(244, 67, 54, 0.1)',
            borderRadius: 1,
          }}
        >
          <Typography sx={{ ...textStyle, fontSize: '1.3rem', color: 'error.main' }}>
            Adventure Failed
          </Typography>
        </Box>
      )

    case JournalEntryType.ModeratorApprovalReceived: {
      const approvalContent = entry.content as Record<string, unknown> | null
      const wasApproved = approvalContent?.Approved ?? approvalContent?.approved
      const denialReason = (approvalContent?.RejectionReason ?? approvalContent?.rejectionReason ?? approvalContent?.Reason ?? approvalContent?.reason) as string | undefined
      const approvalMessage = wasApproved ? 'Moderator approved' : 'Moderator denied'
      const approvalEmoji = wasApproved ? '✅' : '❌'

      return (
        <Box sx={{ py: 0.75 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 1, fontSize: '0.6875rem', color: 'text.disabled',
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500,
          }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            <span>{approvalEmoji}</span>
            <span>{approvalMessage}</span>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>
          {!wasApproved && denialReason && (
            <Typography sx={{
              ...textStyle, fontSize: '0.75rem', color: 'text.secondary',
              textAlign: 'center', mt: 0.5, fontStyle: 'italic',
              textTransform: 'none',
            }}>
              Reason: {denialReason}
            </Typography>
          )}
        </Box>
      )
    }

    default:
      return null
  }
}
